import React from 'react';
import classnames from 'src/util/classnames';
import { Skeleton } from 'src/components/ui';
import { useFormLoading } from './FormLoading';

export type ToggleProps = {
  checked: boolean;
  onChange?: (checked: boolean) => void;
  readOnly?: boolean;
  id?: string;
  title?: string;
  'data-testid'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
};

/**
 * On/off switch drawn like the form builder's inspector toggle. Without `onChange` it is read-only.
 * `!` beats the global `button { cursor: pointer !important }`; `bg-[#fff]` keeps the knob white, since
 * dark mode repaints `.bg-white`.
 */
export function Toggle({
  checked,
  onChange,
  readOnly,
  id,
  title,
  'data-testid': testId,
  'aria-labelledby': labelledBy,
  'aria-describedby': describedBy,
}: ToggleProps) {
  const loading = useFormLoading();
  const editable = !readOnly && !!onChange;

  if (loading) return <Skeleton inline width="2.25rem" height="1.25rem" rounded="full" className="shrink-0" />;

  return (
    <button
      type="button"
      role="switch"
      id={id}
      title={title}
      data-testid={testId}
      aria-checked={checked}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-readonly={editable ? undefined : true}
      aria-disabled={editable ? undefined : true}
      onClick={editable ? () => onChange?.(!checked) : undefined}
      className={classnames(
        'relative inline-flex items-center h-5 w-9 shrink-0 rounded-full transition-colors border-0 p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        checked ? 'bg-primary' : 'bg-zinc-300 dark:bg-zinc-600',
        editable ? 'cursor-pointer' : 'cursor-default! opacity-60'
      )}
    >
      <span
        aria-hidden="true"
        className={classnames(
          'inline-block h-4 w-4 rounded-full bg-[#fff] shadow transform transition-transform',
          checked ? 'translate-x-4.5' : 'translate-x-0.5'
        )}
      />
    </button>
  );
}

export default Toggle;
